import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Send a new link" — the email-verification link again (plan-014 review I-17; owner answer 41).
 *
 * A password sign-up whose first link expired or was lost was stuck on "Verify your email address first", unable to
 * create an organization or get the trial: the first-run task's button was disabled ("arrives soon") and the
 * organization flow offered nothing. `POST /v1/auth/email-verification/resend` exists; this asks it. A new link
 * replaces the old one; `already_verified` refreshes what the page shows; a refusal (too many in a row, or no email
 * sent by WeTalk yet) is said in words. Shown only where the gateway can send (the sandbox sends no email).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@wetalk/ui";
import { type ReactNode } from "react";

import { apiProblemText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import "./mail-landing-gateway.js";

export function ResendVerificationButton(props: {
  /** The query keys whose answer says whether the address is verified, refreshed after `already_verified`. */
  readonly refresh: readonly (readonly string[])[];
  readonly label?: string;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const send = gateway.resendEmailVerification;
  const [said, setSaid] = usePortalMessage();
  const resend = useMutation({
    mutationFn: () => {
      if (send === undefined) throw new Error("unavailable");
      return send();
    },
    onSuccess: async (state) => {
      if (state === "already_verified") {
        setSaid(() => (portalText("core", "mail_landing.your_address_is_already_verified_5714b6")));
        for (const key of props.refresh) await queryClient.invalidateQueries({ queryKey: key });
        return;
      }
      setSaid(() => (portalText("core", "mail_landing.a_new_link_is_on_its_way_earlier_links_no_longer_work_8fe71c")));
    },
    onError: (raised: unknown) => {
      setSaid(() => (apiProblemText(raised, portalText("core", "mail_landing.a_new_link_could_not_be_sent_just_now_309a67"))));
    },
  });
  if (send === undefined) return null;
  return (
    <span className="c-resend-verification">
      <Button
        variant="secondary"
        disabled={resend.isPending}
        onClick={() => {
          setSaid(null);
          resend.mutate();
        }}
      >
        {resend.isPending ? portalText("core", "auth.sending_cf7655") : (props.label ?? portalText("core", "mail_landing.send_a_new_link_2ed491"))}
      </Button>
      {said === null ? null : (
        <span className="c-note" role="status">
          {said}
        </span>
      )}
    </span>
  );
}
